import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderSection } from "@/components/studio/builder-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { ReadOnlyBanner } from "@/components/studio/read-only-banner";
import { RefinementSection } from "@/components/studio/refinement-section";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { useToast } from "@/hooks/use-toast";
// import { useUpload } from "@/hooks/use-upload";
import { ContentAffirmationService } from "@/lib/api/affirmation.service";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type {
  AffirmationContent,
  AffirmationLanguageVariant,
  AffirmationLanguageVariantDraft,
  Category,
  Coach,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { AffirmationVariantFields } from "./affirmation-variant-fields";

const affirmationConfig = CONTENT_TYPE_CONFIG.affirmation;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface AffirmationEditData extends AffirmationContent {
  variants: AffirmationLanguageVariant[];
  authorCoachName?: string | null;
}

export default function AffirmationEditPage({ id }: { id: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  // Background image upload disabled — kept for future reference.
  // const { uploadFile, isUploading } = useUpload();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const [categoryId, setCategoryId] = useState("");
  const [authorCoachId, setAuthorCoachId] = useState<string | null>(null);
  const [internalNotes, setInternalNotes] = useState("");
  const [variants, setVariants] = useState<AffirmationLanguageVariantDraft[]>(
    [],
  );
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const { data: affirmationData, isLoading: isLoadingAffirmation } =
    useQuery<AffirmationEditData>({
      queryKey: ["/content-affirmations", id],
      queryFn: async () => {
        const affirmation = await ContentAffirmationService.findOne(id);
        const versions = (affirmation as any).versions || [];
        const latestVersion = versions[0] || {};
        return {
          ...affirmation,
          variants: latestVersion.languages || [],
          version: latestVersion.version,
          status: latestVersion.status,
          internalNotes: latestVersion.internalNotes,
          authorCoachName: (affirmation as any).author?.name || null,
        } as AffirmationEditData;
      },
    });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  useEffect(() => {
    if (isCoachOnly && selfCoachId && !authorCoachId) {
      setAuthorCoachId(selfCoachId);
    }
  }, [isCoachOnly, selfCoachId, authorCoachId]);

  useEffect(() => {
    if (!affirmationData) return;
    setSelectedTagOptionIds(affirmationData.tagOptionIds);
    setCategoryId((affirmationData as any).categoryId || "");
    setAuthorCoachId((affirmationData as any).authorCoachId ?? null);
    setInternalNotes(affirmationData.internalNotes || "");
    const hydrated = affirmationData.variants.map((v) => ({
      id: v.id,
      languageCode: v.languageCode,
      isDefault: v.isDefault,
      quoteText: v.quoteText,
      authorName: v.authorName ?? null,
      coachId: v.coachId ?? null,
      featuredImageUrl: v.featuredImageUrl ?? null,
      translationStatus: v.translationStatus,
    }));
    setVariants(hydrated);
    setActiveVariantIndex((idx) =>
      Math.min(idx, Math.max(0, hydrated.length - 1)),
    );
  }, [affirmationData?.updatedAt, affirmationData?.id]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const coach = authorCoachId
        ? coaches.find((c) => c.id === authorCoachId)
        : null;
      const resolvedAuthorName =
        coach?.name ??
        (isCoachOnly && authorCoachId === user?.id ? (user?.name ?? null) : null);
      const payload = {
        affirmation: {
          tagOptionIds: selectedTagOptionIds,
          categoryId: categoryId || undefined,
          authorCoachId: authorCoachId || undefined,
          internalNotes: internalNotes || null,
        },
        variants: variants.map((v) => ({
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          quoteText: v.quoteText,
          authorName: resolvedAuthorName,
          coachId: authorCoachId ?? null,
          featuredImageUrl: v.featuredImageUrl,
          translationStatus:
            v.translationStatus ?? (v.isDefault ? "completed" : "pending"),
        })),
      };
      await ContentAffirmationService.update(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-affirmations"] });
      queryClient.invalidateQueries({
        queryKey: ["/content-affirmations", id],
      });
      toast({ title: "Affirmation saved" });
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to save",
        variant: "destructive",
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      await saveMutation.mutateAsync();
      await ContentAffirmationService.submitForReview(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-affirmations"] });
      queryClient.invalidateQueries({
        queryKey: ["/content-affirmations", id],
      });
      toast({ title: "Submitted for approval" });
      setLocation("/studio/affirmations");
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to submit",
        variant: "destructive",
      });
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: async () => {
      return ContentAffirmationService.createVersion(id);
    },
    onSuccess: (data: { id: string; version: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/content-affirmations"] });
      toast({ title: `New version (v${data.version}) created as draft` });
      setLocation(`/studio/affirmations/${data.id}/edit`);
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to create new version",
        variant: "destructive",
      });
    },
  });

  // Background image upload disabled — kept for future reference.
  // const handleFileUpload = async (
  //   e: React.ChangeEvent<HTMLInputElement>,
  //   field: keyof AffirmationLanguageVariantDraft,
  // ) => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;
  //   const result = await uploadFile(file);
  //   if (result) {
  //     const updated = [...variants];
  //     updated[activeVariantIndex] = {
  //       ...updated[activeVariantIndex],
  //       [field]: result.objectPath,
  //     };
  //     setVariants(updated);
  //   }
  // };

  const updateVariant = (
    field: keyof AffirmationLanguageVariantDraft,
    value: any,
  ) => {
    const updated = [...variants];
    updated[activeVariantIndex] = {
      ...updated[activeVariantIndex],
      [field]: value,
    };
    setVariants(updated);
  };

  const addLanguageVariant = (langCode: string) => {
    if (variants.some((v) => v.languageCode === langCode)) return;
    setVariants([
      ...variants,
      {
        languageCode: langCode,
        isDefault: false,
        quoteText: "",
        authorName: null,
        coachId: null,
        featuredImageUrl: null,
      },
    ]);
    setActiveVariantIndex(variants.length);
  };

  const confirmRemoveLanguageVariant = () => {
    if (pendingRemoveIndex === null) return;
    if (variants[pendingRemoveIndex].isDefault) return;
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    const updated = variants.filter((_, i) => i !== pendingRemoveIndex);
    setVariants(updated);
    if (activeVariantIndex >= updated.length) {
      setActiveVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === index }));
    setVariants(updated);
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (tagId: string) => {
    setSelectedTagOptionIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  const activeVariant = variants[activeVariantIndex];

  const { withdrawMutation, isReadOnly, isOwner, canCreateVersion } =
    useContentLifecycle({
    contentType: "affirmation",
    apiPath: "/content-affirmations",
    id,
    status: affirmationData?.status,
    createdBy: affirmationData?.createdBy,
    authorCoachId: (affirmationData as any)?.authorCoachId,
    listRoute: "/studio/affirmations",
    editRoute: (newId) => `/studio/affirmations/${newId}/edit`,
  });

  const primaryQuote =
    variants.find((v) => v.isDefault)?.quoteText || undefined;

  return (
    <BuilderShell
      contentTypeConfig={affirmationConfig}
      mode="edit"
      title={
        primaryQuote
          ? primaryQuote.length > 60
            ? primaryQuote.slice(0, 60) + "..."
            : primaryQuote
          : undefined
      }
      version={affirmationData?.version}
      status={affirmationData?.status}
      isLoading={isLoadingAffirmation}
      notFound={!isLoadingAffirmation && !affirmationData}
      isReadOnly={isReadOnly}
      isSaving={saveMutation.isPending}
      isSubmitting={submitMutation.isPending}
      isCreatingVersion={createVersionMutation.isPending}
      canCreateVersion={canCreateVersion}
      isWithdrawing={withdrawMutation.isPending}
      onSave={() => saveMutation.mutate()}
      onSubmit={() =>
        categoryId
          ? submitMutation.mutate()
          : toast({
              title: "Select a category before submitting for approval.",
              variant: "destructive",
            })
      }
      onCreateVersion={() => createVersionMutation.mutate()}
      onWithdraw={() => withdrawMutation.mutate(undefined)}
    >
      {isReadOnly && <ReadOnlyBanner ownedByOther={!isOwner} />}

      <IntentTaggingSection
        stepNumber={1}
        categories={categories}
        categoryId={categoryId}
        onCategoryChange={setCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={selectedTagOptionIds}
        onToggleTagOption={toggleTagOption}
        isReadOnly={isReadOnly}
      />

      <BuilderSection
        title="Author"
        stepNumber={2}
        description="Select the coach attributed as the author"
        defaultOpen={true}
      >
        <div className="space-y-2">
          <Label>Author</Label>
          {isCoachOnly ? (
            <Input
              value={
                affirmationData?.authorCoachName ??
                (authorCoachId === user?.id ? (user?.name ?? "") : "")
              }
              disabled
              data-testid="select-author-coach"
            />
          ) : (
            <Select
              value={authorCoachId || "none"}
              onValueChange={(val) =>
                setAuthorCoachId(val === "none" ? null : val)
              }
              disabled={isReadOnly}
            >
              <SelectTrigger data-testid="select-author-coach">
                <SelectValue placeholder="Select author" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No author</SelectItem>
                {coaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </BuilderSection>

      <LanguageVariantsSection
        stepNumber={3}
        variants={variants}
        activeVariantIndex={activeVariantIndex}
        onActiveVariantChange={setActiveVariantIndex}
        availableLanguages={availableLanguages}
        onAddVariant={addLanguageVariant}
        onSetDefault={setAsDefault}
        pendingRemoveIndex={pendingRemoveIndex}
        onRequestRemove={setPendingRemoveIndex}
        onConfirmRemove={confirmRemoveLanguageVariant}
        onCancelRemove={() => setPendingRemoveIndex(null)}
        isReadOnly={isReadOnly}
      >
        {activeVariant && (
          <AffirmationVariantFields
            variant={activeVariant}
            onFieldChange={updateVariant}
            // onFileUpload={handleFileUpload}
            // isUploading={isUploading}
            isReadOnly={isReadOnly}
          />
        )}
      </LanguageVariantsSection>

      <RefinementSection
        stepNumber={4}
        internalNotes={internalNotes}
        onInternalNotesChange={setInternalNotes}
        isReadOnly={isReadOnly}
      />
    </BuilderShell>
  );
}
